import React from 'react';
import { Alert, Form, InputNumber, Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/transportCostAllocation';
import { formatUnit } from '../../../utils/units';
import { formatDate } from '../../../utils/dateUtils';
import type { AverageCostCurrency } from '../../../types/pz';
import {
  convertAverageCost,
  type AverageCostUnit,
  type ProductLogisticsInput,
} from '../productLogistics';
import { MAX_AVERAGE_COST_PLN, nonNegativeNumberRule } from './cardFields';
import { formatCostAmount, formatUnitCost } from './cardFormat';
import { getCurrencySymbol } from '../../../utils/currency';
import CardSectionHeader from './CardSectionHeader';

export const AVERAGE_COST_UNITS: AverageCostUnit[] = ['pcs', 'm2', 'm3'];
export const AVERAGE_COST_CURRENCIES: AverageCostCurrency[] = ['PLN', 'EUR', 'USD'];

interface AverageCostSectionProps {
  values: ProductLogisticsInput & {
    averagePurchasePricePLN?: number | null;
    averagePurchasePriceUnit?: AverageCostUnit | null;
    averagePurchasePriceCurrency?: AverageCostCurrency | null;
  };
  disabled: boolean;
  updatedByName?: string | null;
  updatedAt?: string | null;
  /** The saved unit differs and some variant has its own cost. */
  unitChangedWithVariantCosts: boolean;
}

/** "Średni koszt sprzedaży" (estimated cost of goods sold, API field averagePurchasePricePLN): net amount in PLN/EUR/USD per one unit, conversions and who/when. */
const AverageCostSection: React.FC<AverageCostSectionProps> = ({
  values, disabled, updatedByName, updatedAt, unitChangedWithVariantCosts,
}) => {
  const { t } = useTranslation(['products']);
  const price = values.averagePurchasePricePLN;
  const unit = values.averagePurchasePriceUnit ?? null;
  const currency = values.averagePurchasePriceCurrency || 'PLN';
  const formatAmount = (value: number) => formatCostAmount(value, currency);
  const hasPrice = price !== null && price !== undefined && Number.isFinite(Number(price));
  const conversions = convertAverageCost(hasPrice ? Number(price) : null, unit, values);

  const converted: string[] = [];
  if (hasPrice && unit && conversions.perPiece !== null) {
    if (unit !== 'pcs') converted.push(t('products:card.cost.perUnit', { value: formatAmount(conversions.perPiece), unit: formatUnit('pcs') }));
    if (unit !== 'm2' && conversions.perM2 !== null) converted.push(t('products:card.cost.perUnit', { value: formatAmount(conversions.perM2), unit: formatUnit('m2') }));
    if (unit !== 'm3' && conversions.perM3 !== null) converted.push(t('products:card.cost.perUnit', { value: formatAmount(conversions.perM3), unit: formatUnit('m3') }));
    if (conversions.perPallet !== null) converted.push(t('products:card.cost.perPallet', { value: formatAmount(conversions.perPallet) }));
  }

  return (
    <section className="product-card-section">
      <CardSectionHeader
        title={t('products:card.cost.title')}
        subtitle={t('products:card.cost.subtitle')}
        hint={t('products:card.cost.hint')}
      />
      <Form.Item label={t('products:card.cost.netCost')} extra={t('products:card.cost.extra')} required={false}
        className="product-card-cost-field">
        <Space.Compact block>
          <Form.Item name="averagePurchasePricePLN" noStyle
            rules={[nonNegativeNumberRule(t, MAX_AVERAGE_COST_PLN)]}>
            <InputNumber
              className="product-card-number"
              aria-label={t('products:card.cost.netCost')}
              placeholder={t('products:card.cost.placeholder')}
              disabled={disabled}
              controls={false}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
          <Form.Item name="averagePurchasePriceCurrency" noStyle>
            <Select
              className="product-card-cost-currency"
              aria-label={t('products:card.cost.currencyLabel')}
              disabled={disabled}
              options={AVERAGE_COST_CURRENCIES.map((option) => ({ value: option, label: option }))}
            />
          </Form.Item>
          <Form.Item
            name="averagePurchasePriceUnit"
            noStyle
            dependencies={['averagePurchasePricePLN']}
            rules={[({ getFieldValue }) => ({
              validator(_, selected) {
                const current = getFieldValue('averagePurchasePricePLN');
                return current === null || current === undefined || current === '' || selected
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('products:card.cost.unitRequired')));
              },
            })]}
          >
            <Select
              className="product-card-cost-unit"
              aria-label={t('products:card.cost.unitLabel')}
              disabled={disabled}
              options={AVERAGE_COST_UNITS.map((option) => ({
                value: option,
                label: t('products:card.cost.unitOption', { unit: formatUnit(option), currency: getCurrencySymbol(currency) }),
              }))}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      {hasPrice && unit && (
        <div className="product-card-conversions" data-testid="cost-conversions">
          <div className="is-base">{t('products:card.cost.perUnit', { value: formatUnitCost(Number(price), currency), unit: formatUnit(unit) })}</div>
          {converted.map((text) => <div key={text}>{text}</div>)}
          {conversions.perPiece === null && <div className="product-card-muted">{t('products:card.cost.needDimensions')}</div>}
        </div>
      )}

      {currency !== 'PLN' && (
        <p className="product-card-muted product-card-cost-fx" data-testid="cost-currency-hint">
          {t('products:card.cost.currencyHint', { currency })}
        </p>
      )}

      {unitChangedWithVariantCosts && (
        <Alert className="product-card-warning" type="warning" showIcon message={t('products:card.cost.unitChangeWarning')} />
      )}

      <div className="product-card-cost-source">
        {hasPrice ? (
          <>
            <span>{t('products:card.cost.sourceManual')}</span>
            {updatedByName && updatedAt && (
              <span>{t('products:card.cost.changedBy', { name: updatedByName, date: formatDate(updatedAt) })}</span>
            )}
            <span className="product-card-muted">{t('products:card.cost.editors')}</span>
          </>
        ) : (
          <span className="product-card-missing">{t('products:card.cost.missing')}</span>
        )}
      </div>
    </section>
  );
};

export default AverageCostSection;
